/**
 * PaymentMethodSelector (Web)
 *
 * Web-specific implementation that fetches available methods from server
 * and checks Apple Pay client capability.
 */
import React, {useMemo} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';

import {
  PAYMENT_SERVICE_TYPES,
  SERVICE_TYPE_TO_METHOD,
  usePaymentServices,
} from '../../../../queries/paymentCard';
import theme from '../../../../style/theme';
import Text from '../../../themed/Text';

import {useApplePayAvailability} from './applePay/useApplePayAvailability';
import {useGooglePayAvailability} from './googlePay/useGooglePayAvailability';
import PaymentMethodOption from './PaymentMethodOption';
import type {PaymentMethodSelectorProps, PaymentMethodType, SavedCard} from './types';

export default function PaymentMethodSelector({
  selectedMethod,
  onMethodChange,
  onAddCard,
  onChangeCard,
  onDeleteCard,
  isCardActionLoading = false,
  applePayConfig: _applePayConfig,
  onCanPayChange,
}: PaymentMethodSelectorProps) {
  // Fetch payment services from server
  const {data: paymentServicesData, isLoading, error} = usePaymentServices();

  // Derive available methods from server response
  const availableMethods = useMemo((): PaymentMethodType[] => {
    // While loading or on error, show card as default (better UX than empty)
    if (!paymentServicesData?.services) return ['card'];

    const methods: PaymentMethodType[] = [];
    for (const service of paymentServicesData.services) {
      const methodType =
        SERVICE_TYPE_TO_METHOD[service.type as keyof typeof SERVICE_TYPE_TO_METHOD];
      if (methodType && !methods.includes(methodType)) {
        methods.push(methodType);
      }
    }

    // If server returns empty services, fallback to card
    // This handles misconfigured shops - card will show "Add card" prompt
    if (methods.length === 0) {
      methods.push('card');
    }

    return methods;
  }, [paymentServicesData?.services]);

  // Derive saved card from till_payments service
  const savedCard = useMemo((): SavedCard | undefined => {
    const tillService = paymentServicesData?.services.find(
      s => s.type === PAYMENT_SERVICE_TYPES.TILL_PAYMENTS,
    );
    const card = tillService?.saved_card;
    if (!card) return undefined;

    return {
      cardType: card.card_type,
      lastFourDigits: card.obfuscated_card?.slice(-4) ?? '',
      expiryMonth: card.expiry_month,
      expiryYear: card.expiry_year,
    };
  }, [paymentServicesData?.services]);

  // Check Apple Pay client capability (only if server returned apple_pay)
  const applePayAvailability = useApplePayAvailability({
    skip: !availableMethods.includes('applePay'),
  });

  // Check Google Pay client readiness (only if server returned google_pay)
  const googlePayAvailability = useGooglePayAvailability({
    skip: !availableMethods.includes('googlePay'),
  });

  // Apple Pay is offered by the server but the client cannot pay (capability
  // probe finished and canMakePayments is false). Drives the auto-fallback below.
  const applePayUnavailable =
    availableMethods.includes('applePay') &&
    !applePayAvailability.isChecking &&
    !applePayAvailability.canMakePayments;

  // Google Pay is offered by the server but the client cannot pay. The probe is
  // "settled" once it reports support or surfaces an error; until then we do NOT
  // fall back (probe still in flight). Mirrors applePayUnavailable.
  const googlePayProbeSettled =
    googlePayAvailability.isSupported || Boolean(googlePayAvailability.error);
  const googlePayUnavailable =
    availableMethods.includes('googlePay') &&
    googlePayProbeSettled &&
    !googlePayAvailability.isReady;

  // Auto-fallback: if the selected method is not available (or Apple Pay /
  // Google Pay is selected but the client can't pay), switch to card.
  React.useEffect(() => {
    if (isLoading) return;

    const methodAvailable = availableMethods.includes(selectedMethod);
    const applePaySelectedButUnavailable = selectedMethod === 'applePay' && applePayUnavailable;
    const googlePaySelectedButUnavailable = selectedMethod === 'googlePay' && googlePayUnavailable;

    if (!methodAvailable || applePaySelectedButUnavailable || googlePaySelectedButUnavailable) {
      if (availableMethods.includes('card')) {
        onMethodChange('card');
      } else if (availableMethods.length > 0) {
        onMethodChange(availableMethods[0]);
      }
    }
  }, [
    isLoading,
    availableMethods,
    selectedMethod,
    onMethodChange,
    applePayUnavailable,
    googlePayUnavailable,
  ]);

  // Notify parent about payment readiness
  // Payment is NOT ready when card is selected but no card is saved
  React.useEffect(() => {
    if (!onCanPayChange || isLoading) return;

    const canPay = !(selectedMethod === 'card' && !savedCard);
    onCanPayChange(canPay);
  }, [selectedMethod, savedCard, isLoading, onCanPayChange]);

  const handleMethodSelect = (method: PaymentMethodType) => {
    // Don't allow selecting unavailable Apple Pay
    if (method === 'applePay' && applePayUnavailable) {
      return;
    }
    // Don't allow selecting Google Pay until the readiness probe reports isReady
    if (method === 'googlePay' && !googlePayAvailability.isReady) {
      return;
    }
    if (method === 'card' && !savedCard && onAddCard) {
      onAddCard();
    }
    onMethodChange(method);
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Choose Payment Method</Text>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colour.uiControls?.primary ?? '#0054A6'} />
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Choose Payment Method</Text>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Unable to load payment methods</Text>
          <Text style={styles.errorSubtext}>Please try again later</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Choose Payment Method</Text>

      <View style={styles.optionsList}>
        {availableMethods.includes('card') && (
          <PaymentMethodOption
            type="card"
            isSelected={selectedMethod === 'card'}
            onSelect={() => handleMethodSelect('card')}
            savedCard={savedCard}
            onChangeCard={onChangeCard}
            onDeleteCard={onDeleteCard}
            isCardActionLoading={isCardActionLoading}
          />
        )}

        {/* Apple Pay tile is shown only when the client can actually pay. While
            capability is still being probed canMakePayments is false, so the tile
            stays hidden (no flash of a greyed, unclickable option). */}
        {availableMethods.includes('applePay') && applePayAvailability.canMakePayments ? (
          <PaymentMethodOption
            type="applePay"
            isSelected={selectedMethod === 'applePay'}
            onSelect={() => handleMethodSelect('applePay')}
          />
        ) : null}

        {/* Google Pay tile is shown only when the readiness probe resolves
            isReady=true. isSupported alone never gates visibility — a supported
            but not-ready client would open a sheet that rejects. While the probe
            is in flight isReady is false, so the tile stays hidden (no flash). */}
        {availableMethods.includes('googlePay') && googlePayAvailability.isReady ? (
          <PaymentMethodOption
            type="googlePay"
            isSelected={selectedMethod === 'googlePay'}
            onSelect={() => handleMethodSelect('googlePay')}
          />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  optionsList: {
    gap: 12,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  errorContainer: {
    padding: 40,
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#DC2626',
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 14,
    color: '#666',
  },
});
